/**
 * 모바일 이미지 업로드 유틸리티
 * 웹(@ict/web)과 동일한 API 사용
 * 오프라인 시 Capacitor Filesystem + IndexedDB에 로컬 저장
 */

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { createLogger } from "@ict/shared";
import { saveLocalPhoto } from "./db/photos";
import { addToSyncQueue } from "./db/changes";
import { getAuthToken, forceLogout } from "./apollo";
import { compressImage } from "./image-compress";

const logger = createLogger({ module: "Upload" });

// API 서버 URL
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:23998";

/**
 * 업로드 요청 파라미터
 */
export interface UploadParams {
  file: File;
  type: "checkItem" | "document" | "chart" | "photoEtc" | "contact";
  projectId?: string;
  docKey?: string;
  machineId?: string;
  checkId?: string;
  imageX?: "X";
  imageO?: "O";
}

/**
 * 업로드 응답
 */
export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
  errorCode?:
    | "UNAUTHORIZED"
    | "INVALID_FILE_TYPE"
    | "FILE_TOO_LARGE"
    | "UPLOAD_FAILED"
    | "OFFLINE_SAVED"
    | "STORAGE_QUOTA_EXCEEDED";
  /** 로컬 저장 여부 (오프라인 시 true) */
  isLocal?: boolean;
  /** 로컬 사진 ID (오프라인 저장 시) */
  localPhotoId?: string;
}

/**
 * base64 DataURL을 File 객체로 변환
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * 이미지 업로드 함수
 * Bearer 토큰 + 쿠키 기반 인증 사용
 *
 * @param params 업로드 파라미터
 * @returns 업로드 결과 (성공 시 URL 포함)
 */
export async function uploadImage(params: UploadParams): Promise<UploadResult> {
  const formData = new FormData();
  formData.append("file", params.file);
  formData.append("type", params.type);
  if (params.projectId) formData.append("projectId", params.projectId);

  if (params.docKey) formData.append("docKey", params.docKey);
  if (params.machineId) formData.append("machineId", params.machineId);
  if (params.checkId) formData.append("checkId", params.checkId);
  if (params.imageX) formData.append("imageX", params.imageX);
  if (params.imageO) formData.append("imageO", params.imageO);

  try {
    // 토큰 확인 (조기 반환으로 불필요한 네트워크 요청 방지)
    const token = getAuthToken();
    if (!token) {
      return {
        success: false,
        error: "인증 토큰이 없습니다. 다시 로그인해주세요.",
        errorCode: "UNAUTHORIZED",
      };
    }

    // Authorization 헤더 추가 (Bearer 토큰)
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(`${API_URL}/upload`, {
      method: "POST",
      body: formData,
      credentials: "include", // 쿠키도 함께 전송 (웹 호환)
      headers,
    });

    // HTTP 에러 상태 체크
    if (!response.ok) {
      if (response.status === 401) {
        // 토큰 만료 시 전체 정리 후 로그인 페이지로 이동
        forceLogout();
        return {
          success: false,
          error: "인증이 필요합니다. 다시 로그인해주세요.",
          errorCode: "UNAUTHORIZED",
        };
      }
      if (response.status === 413) {
        const body = await response.json().catch(() => null);
        return {
          success: false,
          error: body?.error || "스토리지 용량이 초과되었습니다.",
          errorCode: "STORAGE_QUOTA_EXCEEDED",
        };
      }
      return {
        success: false,
        error: `업로드 실패: ${response.status} ${response.statusText}`,
        errorCode: "UPLOAD_FAILED",
      };
    }

    return response.json();
  } catch (error) {
    logger.error("이미지 업로드 오류:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "네트워크 오류",
      errorCode: "UPLOAD_FAILED",
    };
  }
}

/**
 * File을 base64 DataURL로 변환
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * base64 데이터에서 파일 크기 계산
 */
export function getBase64FileSize(base64: string): number {
  // base64 문자열에서 실제 데이터 부분만 추출
  const base64Data = base64.split(",")[1] || base64;
  // base64 인코딩은 3바이트를 4문자로 표현하므로 역계산
  return Math.ceil((base64Data.length * 3) / 4);
}

/**
 * 이미지 리사이즈 (썸네일 생성용)
 * 메모리 효율을 위해 미리보기용 작은 이미지 생성
 */
export async function resizeImageForThumbnail(
  dataUrl: string,
  maxWidth: number = 200,
  maxHeight: number = 200
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;

      // 비율 유지하며 리사이즈
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * 오프라인 사진 저장 공통 함수
 * 네이티브: Capacitor Filesystem에 저장
 * 웹: IndexedDB에 base64 데이터로 저장
 */
interface OfflineSaveParams {
  dataUrl: string;
  filename: string;
  projectId: string;
  docKey: string;
  machineId: string;
  mimeType?: string;
  /** 동기화 큐 페이로드 (checkItem 또는 photoEtc) */
  syncPayload: Record<string, unknown>;
}

async function savePhotoOffline(params: OfflineSaveParams): Promise<UploadResult> {
  const {
    dataUrl,
    filename,
    projectId,
    docKey,
    machineId,
    mimeType = "image/jpeg",
    syncPayload,
  } = params;

  try {
    // 썸네일 생성 (미리보기용)
    const thumbnail = await resizeImageForThumbnail(dataUrl);
    const machineCompositeId = `${projectId}_${machineId}`;
    const fileSize = getBase64FileSize(dataUrl);

    let localPath = "";
    let localUrl: string;

    // 네이티브: Capacitor Filesystem에 파일 저장
    // 웹: IndexedDB에만 저장 (Filesystem 동적 import가 오프라인에서 실패)
    if (Capacitor.isNativePlatform()) {
      const base64Data = dataUrl.split(",")[1] || dataUrl;
      const filePath = `photos/${filename}`;

      const writeResult = await Filesystem.writeFile({
        path: filePath,
        data: base64Data,
        directory: Directory.Data,
        recursive: true,
      });

      localPath = writeResult.uri;
      localUrl = Capacitor.convertFileSrc(writeResult.uri);
    } else {
      // 웹 환경: base64 데이터를 IndexedDB에 저장하고 썸네일을 미리보기로 사용
      localPath = `indexeddb://${filename}`; // 가상 경로 (실제 데이터는 base64Data 필드)
      localUrl = thumbnail;
    }

    // IndexedDB에 메타데이터 저장
    const photoId = await saveLocalPhoto({
      machineId: machineCompositeId,
      projectId,
      localPath,
      base64Thumbnail: thumbnail,
      base64Data: Capacitor.isNativePlatform() ? undefined : dataUrl, // 웹에서만 원본 저장
      fileName: filename,
      mimeType,
      fileSize,
      synced: false,
    });

    // 동기화 큐에 추가
    await addToSyncQueue({
      type: "photo",
      entityId: photoId,
      operation: "create",
      projectId,
      docKey,
      machineId,
      payload: {
        photoId,
        localPath,
        ...syncPayload,
      },
    });

    return {
      success: true,
      url: localUrl,
      isLocal: true,
      localPhotoId: photoId,
      errorCode: "OFFLINE_SAVED",
    };
  } catch (error) {
    logger.error("오프라인 이미지 저장 실패:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "오프라인 저장 실패",
      errorCode: "UPLOAD_FAILED",
    };
  }
}

/**
 * 점검사진 업로드 헬퍼 함수
 * - 온라인: 서버에 직접 업로드
 * - 오프라인: Capacitor Filesystem에 저장 후 동기화 큐에 추가
 */
export async function uploadCheckItemImage(
  file: File,
  params: {
    projectId: string;
    docKey: string;
    machineId: string;
    checkId: string;
    fieldName: "image" | "imageO" | "imageX";
  }
): Promise<UploadResult> {
  const timestamp = Date.now();
  const filename = `check_${params.checkId}_${params.fieldName}_${timestamp}.jpg`;

  // 이미지 압축
  const compressed = await compressImage(file);

  // 오프라인 체크
  if (!navigator.onLine) {
    const dataUrl = await fileToDataUrl(compressed);
    return savePhotoOffline({
      dataUrl,
      filename,
      projectId: params.projectId,
      docKey: params.docKey,
      machineId: params.machineId,
      syncPayload: {
        checkId: params.checkId,
        fieldName: params.fieldName,
      },
    });
  }

  // 온라인: 압축된 파일 직접 업로드
  const uploadParams: UploadParams = {
    file: compressed,
    type: "checkItem",
    projectId: params.projectId,
    docKey: params.docKey,
    machineId: params.machineId,
    checkId: params.checkId,
  };

  // 적합/부적합 이미지 구분
  if (params.fieldName === "imageO") {
    uploadParams.imageO = "O";
  } else if (params.fieldName === "imageX") {
    uploadParams.imageX = "X";
  }

  return uploadImage(uploadParams);
}

/**
 * 사진대지 업로드 헬퍼 함수
 * - 온라인: 서버에 직접 업로드
 * - 오프라인: IndexedDB/Filesystem에 저장 후 동기화 큐에 추가
 */
export async function uploadOtherPhoto(
  file: File,
  params: {
    projectId: string;
    docKey: string;
    machineId: string;
  }
): Promise<UploadResult> {
  // 이미지 압축
  const compressed = await compressImage(file);

  // 오프라인 체크
  if (!navigator.onLine) {
    const timestamp = Date.now();
    const filename = `photo_etc_${params.machineId}_${timestamp}_${file.name}`;

    // 압축된 File → base64 변환
    const dataUrl = await fileToDataUrl(compressed);

    return savePhotoOffline({
      dataUrl,
      filename,
      projectId: params.projectId,
      docKey: params.docKey,
      machineId: params.machineId,
      mimeType: compressed.type || "image/jpeg",
      syncPayload: {
        uploadType: "photoEtc",
      },
    });
  }

  // 온라인: 압축된 파일 업로드
  return uploadImage({
    file: compressed,
    type: "photoEtc",
    projectId: params.projectId,
    docKey: params.docKey,
    machineId: params.machineId,
  });
}

/**
 * 문의하기 이미지 업로드
 * base64 DataURL 또는 File을 CDN에 업로드하고 URL 반환
 * CS 이미지는 서버/클라이언트 모두 압축 없이 원본 그대로 S3 저장 (cs/ prefix, 180일 후 삭제)
 */
export async function uploadContactImage(
  input: File | string,
  filename?: string
): Promise<UploadResult> {
  let file: File;

  if (typeof input === "string") {
    // base64 DataURL → File 변환
    file = dataUrlToFile(input, filename || `contact-${Date.now()}.jpg`);
  } else {
    file = input;
  }

  // CS 이미지: 서버/클라이언트 모두 압축 없이 원본 저장 (cs/ prefix)
  return uploadImage({
    file,
    type: "contact",
  });
}
